import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import { withRouter } from "react-router-dom";

import { PIN_TYPE } from "../../util/common";
import { SCREENS } from "./util/screens";

import {
  CustomerInfoScreen,
  PreferredCommunicationChannelScreen,
  InfoScreen,
  ScheduledServicesScreen,
  OptionalItemsScreen,
  OtherItemsScreen,
  SignatureScreen,
  ThankYouScreen,
} from "./screens";
import { getEmail, getPhoneNumber } from "../../util/common";

import Service from "./service";

class DeskCommunication extends Component {
  constructor(props) {
    super(props);

    const { communication, settings } = props;

    const agreements = communication.agreements.filter(a => (communication.is_checking_out ? a.visible_desk_check_out : a.visible_desk_check_in));
    const warrantyInterventionIds = [];

    const declinedInterventionIds = [];

    communication.appointment.interventions.forEach(intervention => {
      if (settings.cannot_decline_warranty_types && intervention.pin_type_id === PIN_TYPE.WARRANTY) warrantyInterventionIds.push(intervention.id);
      else if (!intervention.customer_ok || (intervention.is_declined_by_customer && !intervention.mechanic_fixed)) declinedInterventionIds.push(intervention.id);
    });

    this.state = {
      currentScreen: settings.is_desk_contact_info_enabled ? SCREENS.FIRST_SCREEN : SCREENS.INFO,
      communication_status: communication.status,
      approvedMandatoryAgreements:
        agreements.length === 0 || !agreements.some(a => (communication.is_checking_out ? !a.optional_desk_check_out : !a.optional_desk_check_in) && !a.accepted),
      customerEmail: this.getCustomerEmail(),
      customerPhoneNumber: this.getCustomerPhoneNumber(),
      preferredCommChannel: settings.has_sms || settings.has_whats_app ? communication.customer?.preferred_communication_channel : null,
      agreements,
      showAgreementError: false,
      warrantyInterventionIds,
      declinedInterventionIds,
      criticalItems: communication.results ? OptionalItemsScreen.filterCriticalItems(communication.results) : [],
      advisedItems: communication.results ? OptionalItemsScreen.filterAdvisedItems(communication.results) : [],
      workingItems: OtherItemsScreen.filterWorkingsItems(communication.results, props.t),
    };
  }

  getCustomerEmail = () => {
    const { communication } = this.props;

    return communication.correct_email || getEmail(communication);
  };

  getCustomerPhoneNumber = () => {
    const { communication } = this.props;

    return communication.correct_phone || getPhoneNumber(communication);
  };

  handleUpdateCustomerEmail = customerEmail => {
    this.setState({ customerEmail });
  };

  handleUpdateCustomerPhoneNumber = customerPhoneNumber => {
    this.setState({ customerPhoneNumber });
  };

  handleSetPreferredCommChannel = preferredCommChannel => {
    this.setState({ preferredCommChannel });
  };

  isPreferredCommChannelScreenVisible = () => {
    const { settings } = this.props;

    return Boolean(settings.is_desk_contact_info_enabled && (settings.has_sms || settings.has_whats_app));
  };

  handleGoToNextScreen = () => {
    const { currentScreen, criticalItems, advisedItems, workingItems } = this.state;
    const { interventions } = this.props.communication.appointment;
    let shift = 1;

    if (currentScreen + shift === SCREENS.PREFERRED_COMMUNICATION_CHANNEL_SCREEN && !this.isPreferredCommChannelScreenVisible()) ++shift;
    if (currentScreen + shift === SCREENS.SCHEDULED_SERVICES && !interventions.length) ++shift;
    if (currentScreen + shift === SCREENS.CRITICAL_ITEMS && !criticalItems.length) ++shift;
    if (currentScreen + shift === SCREENS.ADVISED_ITEMS && !advisedItems.length) ++shift;
    if (currentScreen + shift === SCREENS.OTHER_ITEMS && !workingItems.length) ++shift;

    this.setState(prevState => ({
      currentScreen: prevState.currentScreen + shift,
    }));
  };

  handleGoToPreviousScreen = () => {
    const { currentScreen, criticalItems, advisedItems, workingItems } = this.state;
    const { interventions } = this.props.communication.appointment;
    let shift = 1;

    if (currentScreen - shift === SCREENS.OTHER_ITEMS && !workingItems.length) ++shift;
    if (currentScreen - shift === SCREENS.ADVISED_ITEMS && !advisedItems.length) ++shift;
    if (currentScreen - shift === SCREENS.CRITICAL_ITEMS && !criticalItems.length) ++shift;
    if (currentScreen - shift === SCREENS.SCHEDULED_SERVICES && !interventions.length) ++shift;
    if (currentScreen - shift === SCREENS.PREFERRED_COMMUNICATION_CHANNEL_SCREEN && !this.isPreferredCommChannelScreenVisible()) ++shift;

    this.setState(prevState => ({
      currentScreen: prevState.currentScreen - shift,
    }));
  };

  handleAgreed = id => {
    let { agreements } = this.state;
    const { communication } = this.props;

    let agreement = agreements.find(a => a.id === id);
    if (agreement) {
      agreement.accepted = !agreement.accepted;
      this.setState({
        agreements: [...agreements],
        approvedMandatoryAgreements: !agreements.some(a => (communication.is_checking_out ? !a.optional_desk_check_out : !a.optional_desk_check_in) && !a.accepted),
      });
    }
  };

  handleSendAnswer = (signature_url, signature_name) => {
    const { is_customer_ok_read_only_in_desk_check_in } = this.props.settings;
    let {
      agreements,
      declinedInterventionIds: declined_intervention_ids,
      criticalItems,
      advisedItems,
      workingItems,
      customerEmail,
      customerPhoneNumber,
      preferredCommChannel,
    } = this.state;

    const correct_email = this.getCustomerEmail();
    const correct_phone = this.getCustomerPhoneNumber();

    const payload = {
      dc_key: this.props.routeKey,
      agreement_ids: agreements.filter(a => a.accepted).map(a => a.id),
      signature_url,
      signature_name,
      correct_email: correct_email === customerEmail ? "" : customerEmail,
      correct_phone: correct_phone === customerPhoneNumber ? "" : customerPhoneNumber,
      preferred_communication_channel: preferredCommChannel,
      ...(is_customer_ok_read_only_in_desk_check_in ? {} : { declined_intervention_ids }),
      results: [...criticalItems, ...advisedItems, ...workingItems],
    };

    return Service.sendAnswer(payload);
  };

  handleShowAgreementError = showAgreementError => {
    this.setState({ showAgreementError });
  };

  handleUpdateDeclinedInterventionIds = (evt, interventionID) => {
    evt.stopPropagation();

    if (this.props.settings.is_customer_ok_read_only_in_desk_check_in || this.state.warrantyInterventionIds.includes(interventionID)) return;

    this.setState(({ declinedInterventionIds }) => {
      if (this.state.declinedInterventionIds.includes(interventionID)) return { declinedInterventionIds: declinedInterventionIds.filter(id => id !== interventionID) };

      return { declinedInterventionIds: [...declinedInterventionIds, interventionID] };
    });
  };

  handleCriticalItemsChanged = criticalItems => {
    this.setState({ criticalItems: [...criticalItems] });
  };

  handleAdvisedItemsChanged = advisedItems => {
    this.setState({ advisedItems: [...advisedItems] });
  };

  render() {
    const {
      customerEmail,
      customerPhoneNumber,
      preferredCommChannel,
      currentScreen,
      agreements,
      approvedMandatoryAgreements,
      declinedInterventionIds,
      showAgreementError,
      criticalItems,
      advisedItems,
      workingItems,
    } = this.state;
    const { communication, settings, routeKey } = this.props;

    const readOnly = communication.is_receptionist;

    switch (currentScreen) {
      default:
      case SCREENS.CUSTOMER_INFO:
        return (
          <CustomerInfoScreen
            communication={communication}
            settings={settings}
            customerEmail={customerEmail}
            customerPhoneNumber={customerPhoneNumber}
            onUpdateCustomerEmail={this.handleUpdateCustomerEmail}
            onUpdateCustomerPhoneNumber={this.handleUpdateCustomerPhoneNumber}
            onGoToNextScreen={this.handleGoToNextScreen}
          />
        );

      case SCREENS.PREFERRED_COMMUNICATION_CHANNEL_SCREEN:
        return (
          <PreferredCommunicationChannelScreen
            communication={communication}
            settings={settings}
            preferredCommChannel={preferredCommChannel}
            onSetPreferredCommChannel={this.handleSetPreferredCommChannel}
            onGoToPreviousScreen={this.handleGoToPreviousScreen}
            onGoToNextScreen={this.handleGoToNextScreen}
          />
        );

      case SCREENS.INFO:
        return (
          <InfoScreen
            communication={communication}
            settings={settings}
            customerEmail={customerEmail}
            customerPhoneNumber={customerPhoneNumber}
            preferredCommChannel={preferredCommChannel}
            onGoToPreviousScreen={this.handleGoToPreviousScreen}
            onGoToNextScreen={this.handleGoToNextScreen}
          />
        );

      case SCREENS.THANK_YOU:
        return <ThankYouScreen />;

      case SCREENS.SCHEDULED_SERVICES:
        return (
          <ScheduledServicesScreen
            communication={communication}
            settings={settings}
            agreements={agreements}
            onAgreed={this.handleAgreed}
            readOnly={readOnly || communication.is_checking_out}
            approved={approvedMandatoryAgreements}
            showAgreementError={showAgreementError}
            onGoToNextScreen={this.handleGoToNextScreen}
            onGoToPreviousScreen={this.handleGoToPreviousScreen}
            declinedInterventionIds={declinedInterventionIds}
            onUpdateDeclinedInterventionIDs={this.handleUpdateDeclinedInterventionIds}
          />
        );

      case SCREENS.CRITICAL_ITEMS:
        return (
          <OptionalItemsScreen
            communication={communication}
            question_status={OptionalItemsScreen.QUESTION_STATUS.CRITICAL}
            readOnly={readOnly || communication.is_checking_out}
            settings={settings}
            showMobile={true}
            items={criticalItems}
            showNotFixedButton={true}
            updateItems={this.handleCriticalItemsChanged}
            onGoToNextScreen={this.handleGoToNextScreen}
            onGoToPreviousScreen={this.handleGoToPreviousScreen}
          />
        );

      case SCREENS.ADVISED_ITEMS:
        return (
          <OptionalItemsScreen
            communication={communication}
            question_status={OptionalItemsScreen.QUESTION_STATUS.ADVISED}
            readOnly={readOnly || communication.is_checking_out}
            settings={settings}
            showMobile={true}
            items={advisedItems}
            showNotFixedButton={true}
            updateItems={this.handleAdvisedItemsChanged}
            onGoToNextScreen={this.handleGoToNextScreen}
            onGoToPreviousScreen={this.handleGoToPreviousScreen}
          />
        );

      case SCREENS.OTHER_ITEMS:
        return (
          <OtherItemsScreen
            communication={communication}
            workingItems={workingItems}
            onGoToNextScreen={this.handleGoToNextScreen}
            onGoToPreviousScreen={this.handleGoToPreviousScreen}
          />
        );

      case SCREENS.SIGNATURE:
        return (
          <SignatureScreen
            optionalItems={[...criticalItems, ...advisedItems]}
            routeKey={routeKey}
            readOnly={readOnly}
            communication={communication}
            onSendAnswer={this.handleSendAnswer}
            onGoToPreviousScreen={this.handleGoToPreviousScreen}
            onGoToNextScreen={this.handleGoToNextScreen}
            settings={settings}
            agreements={agreements}
            onAgreed={this.handleAgreed}
            approved={approvedMandatoryAgreements}
            showAgreementError={showAgreementError}
            onShowAgreementError={this.handleShowAgreementError}
            declinedInterventionIds={declinedInterventionIds}
            onUpdateDeclinedInterventionIDs={this.handleUpdateDeclinedInterventionIds}
          />
        );
    }
  }
}

export default withRouter(withTranslation()(DeskCommunication));
